import { useState } from 'react'
import type { AgentFinding } from '../../types/workbench'

// HOW EACH AGENT'S FINDINGS ARE LAID OUT (owner, 2026-10-13): "comms findings need to show as nested tree view of tiles like email threads
// and communication threads in some creative manner. strategy findings need to be branches of a honeycomb system of connected strategies.
// researcher findings are fine as is but the tiles need better spacing and padding."
//
// The Researcher: a column of tiles with room inside. The Comms agent: a TREE — the person at the root, a branch per channel (threads,
// meetings, notes, documents, the CRM), the tiles as leaves hanging off a rail with an elbow each, the way a mail client draws a thread.
// The Strategy agent: a HONEYCOMB — each strategy a hexagonal cell, the cells packed so every one touches its neighbours, the one in hand
// opened in full beneath with the button to use it. Every tile keeps the one button that applies it to the email.

export function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, '') } catch { return url }
}

/** One finding as a tile: the text, where it came from, and — anchored in the tile's corner — the one button that applies it to the email. */
export function FindingTile({ f, onApply, busy, compact = false }: { f: AgentFinding; onApply?: () => void; busy?: boolean; compact?: boolean }) {
  return (
    <div className={`agent-finding${onApply ? ' appliable' : ''}${compact ? ' compact' : ''}`}>
      <p className="agent-finding-text">{f.text}</p>
      <p className="muted small agent-finding-meta">
        {f.source && <span className={`fact-source kind-${f.source}`}>{f.source}</span>}
        {f.url && <> · <a href={f.url} target="_blank" rel="noreferrer">{hostOf(f.url)}</a></>}
        {f.when && <> · {f.when}</>}
      </p>
      {onApply && <button type="button" className="button small agent-use" onClick={onApply} disabled={busy} title="Hands this to the Wordsmith as an instruction; the draft follows">Use in the email</button>}
    </div>
  )
}

/** The Researcher: tiles in a column, room inside each. */
export function ResearcherList({ results, onApply, busy }: { results: AgentFinding[]; onApply: (f: AgentFinding) => void; busy?: boolean }) {
  return (
    <ul className="plain agent-findings researcher-list">
      {results.map((f, i) => <li key={i}><FindingTile f={f} onApply={f.apply ? () => onApply(f) : undefined} busy={busy} /></li>)}
      {results.length === 0 && <li className="muted small">Nothing yet.</li>}
    </ul>
  )
}

/* ── the Comms tree ── */
const BRANCHES: { key: string; name: string; test: (f: AgentFinding) => boolean }[] = [
  { key: 'threads', name: 'Email threads', test: (f) => (f.source ?? '') === 'mail' || /^thread\b/i.test(f.text) },
  { key: 'meetings', name: 'Meetings', test: (f) => (f.source ?? '') === 'calendar' || /^(meeting|in the room|transcript)\b/i.test(f.text) },
  { key: 'notes', name: 'Notes', test: (f) => (f.source ?? '') === 'note' || /^note\b/i.test(f.text) },
  { key: 'documents', name: 'Documents', test: (f) => (f.source ?? '') === 'document' || /^(document|file|attachment)\b/i.test(f.text) },
  { key: 'crm', name: 'The CRM', test: (f) => (f.source ?? '') === 'crm' },
]

/** The Comms agent: the person at the root, a branch per channel, each communication a tile on the branch's rail — newest first as they came. */
export function CommsTree({ results, onApply, busy, personName }: { results: AgentFinding[]; onApply: (f: AgentFinding) => void; busy?: boolean; personName?: string | null }) {
  if (results.length === 0) return <p className="muted small">Nothing yet.</p>
  const taken = new Set<number>()
  const branches = BRANCHES.map((b) => ({ ...b, items: results.map((f, i) => ({ f, i })).filter(({ f, i }) => !taken.has(i) && b.test(f) && (taken.add(i), true)) })).filter((b) => b.items.length > 0)
  const rest = results.map((f, i) => ({ f, i })).filter(({ i }) => !taken.has(i))
  if (rest.length > 0) branches.push({ key: 'other', name: 'Other', test: () => true, items: rest })
  return (
    <div className="comms-tree" role="tree" aria-label="Communications, by channel">
      <div className="comms-root"><span className="comms-root-dot" aria-hidden="true" /><strong>{personName ?? 'Everything with them'}</strong><span className="muted small"> · {results.length} {results.length === 1 ? 'communication' : 'communications'} that bear on this</span></div>
      <ul className="plain comms-branches">
        {branches.map((b) => (
          <li key={b.key} className={`comms-branch branch-${b.key}`} role="treeitem" aria-expanded="true">
            <div className="comms-branch-head"><span className="comms-elbow" aria-hidden="true" /><span className="tag shared">{b.name}</span><span className="muted small">{b.items.length}</span></div>
            <ul className="plain comms-leaves" role="group">
              {b.items.map(({ f, i }) => (
                <li key={i} className="comms-leaf" role="treeitem">
                  <span className="comms-elbow" aria-hidden="true" />
                  <FindingTile f={f} onApply={f.apply ? () => onApply(f) : undefined} busy={busy} compact />
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ── the Strategy honeycomb ── */
/** The first few words of a finding as a cell's title: up to the first colon or dash, or six words. */
export function cellTitle(text: string): string {
  const head = text.split(/[:—–-]\s/)[0].trim()
  const words = head.split(/\s+/)
  return words.length <= 7 ? head : words.slice(0, 6).join(' ') + '…'
}

/** The Strategy agent: every strategy a hexagonal cell packed against its neighbours; pick a cell and it opens in full beneath, with the button. */
export function StrategyHoneycomb({ results, onApply, busy }: { results: AgentFinding[]; onApply: (f: AgentFinding) => void; busy?: boolean }) {
  const [open, setOpen] = useState(0)
  if (results.length === 0) return <p className="muted small">Nothing yet.</p>
  const at = Math.min(open, results.length - 1)
  const picked = results[at]
  return (
    <div className="honeycomb-wrap">
      <ul className="plain honeycomb" role="listbox" aria-label="Strategies, connected">
        {results.map((f, i) => (
          <li key={i} className={`hex-cell${i === at ? ' on' : ''}${f.apply ? ' appliable' : ''}`} role="option" aria-selected={i === at}>
            <button type="button" className="hex-button" onClick={() => setOpen(i)} title={f.text}>
              <span className="hex-source">{f.source ?? 'strategy'}</span>
              <span className="hex-title">{cellTitle(f.text)}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="hex-open">
        <FindingTile f={picked} onApply={picked.apply ? () => onApply(picked) : undefined} busy={busy} />
      </div>
    </div>
  )
}
